import {
  BaseModal,
  Button,
  FileUploader,
  Input,
  ToastMessage
} from '@cokitchen/cokitchen-components';
import React, { useEffect, useState } from 'react';
import { apiService } from 'services/base.service';
import { toast } from 'react-toastify';
import Select from 'components/base/select';
import { ENUM_TIER_NAME } from 'utils/enum';
import { FC_POINTS_THRESHOLDS, STORAGE_KEYS } from 'utils/constants';
import { EMPTY_FORM_DATA, TIER_NAME_OPTIONS } from '../utils';
import { MealType, MultiselectOptionsType, RewardType } from 'utils/types';
import { getComplimentaryMeals, getMealNameAndImages } from 'utils/functions';

const CreateLevelModal = ({
  show,
  onClose
}: {
  show: boolean;
  onClose: (success?: boolean) => void;
}) => {
  const COMPLIMENTARY = 'comp';
  const [formData, setFormData] = useState(EMPTY_FORM_DATA);
  const [loading, setLoading] = useState(false);
  const [complimentaryMeals, setComplimentaryMeals] = useState<MealType[]>([]);
  const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN) as string;

  const onConfirm = async () => {
    setLoading(true);
    try {
      const res = await apiService.post(`internal/loyalty-rewards-level`, {
        data: formData,
        isInternal: true
      });
      if (res?.data) {
        toast(<ToastMessage type='success' message={`${formData.title} created successfully`} />);
        setFormData(EMPTY_FORM_DATA);
        onClose(true);
      }
    } catch (err: any) {
      toast(<ToastMessage type='error' message={`Error encountered while creating level`} />);
    } finally {
      setLoading(false);
    }
  };

  const fetchComplimentaryMeals = async () => {
    await getComplimentaryMeals(setComplimentaryMeals);
  };

  useEffect(() => {
    fetchComplimentaryMeals();
  }, []);

  const isDisabled = !formData.title || !formData.max_points;

  const handleTitleChange = (v: string) => {
    const tierName = v as ENUM_TIER_NAME;
    setFormData({
      ...formData,
      title: tierName,
      max_points: FC_POINTS_THRESHOLDS[tierName]
    });
  };

  const getRewardValue = (rewardType: RewardType) => {
    const rewardValue =
      formData.rewards.find((reward) => reward.reward_type === rewardType)?.reward_quantity ?? '';
    return rewardValue;
  };

  const handleRewardChange = (e: React.ChangeEvent<HTMLInputElement>, rewardType: RewardType) => {
    const rewards = [...formData.rewards];
    const rewardIndex = rewards.findIndex((reward) => reward.reward_type === rewardType);
    if (rewardIndex !== -1) {
      rewards[rewardIndex] = {
        ...rewards[rewardIndex],
        reward_quantity: parseInt(e.target.value)
      };
      setFormData({ ...formData, rewards });
    }
  };

  const mealsList: MultiselectOptionsType[] = complimentaryMeals
    .filter((meal) => meal.name.toLowerCase().startsWith(COMPLIMENTARY))
    .map((meal) => ({
      text: meal.name,
      value: meal.id
    }));
  return (
    <div>
      <BaseModal
        isOpen={show}
        title='Create new level'
        onRequestClose={() => onClose()}
        footer={
          <Button
            onClick={onConfirm}
            color='primary'
            variant='block'
            loading={loading}
            disabled={isDisabled}
            small>
            Confirm
          </Button>
        }>
        <div>
          <p className='text-sm font-medium mb-2'>Select Tier Name</p>
          <Select
            hasSearch
            placeholder='e.g Foodie'
            value={formData.title}
            options={TIER_NAME_OPTIONS}
            onChange={handleTitleChange}
          />
        </div>
        <Input
          readOnly
          small
          label='Fc Points Threshold'
          type='number'
          placeholder='e.g 200,000'
          className='w-full mt-6 text-sm mb-3'
          inputClass='px-[20px] py-[14px]'
          value={formData.max_points ?? ''}
        />
        <div className='grid grid-cols-2 gap-2'>
          <Input
            small
            label='Order Discount (%)'
            type='number'
            placeholder='e.g 20'
            className='w-full text-sm mb-3'
            inputClass='px-[20px] py-[14px]'
            value={getRewardValue('Discount')}
            onChange={(e) => handleRewardChange(e, 'Discount')}
            onWheel={(e) => e.currentTarget.blur()}
          />
          <Input
            small
            label='Free Deliveries'
            type='number'
            placeholder='e.g 2'
            className='w-full text-sm mb-3'
            inputClass='px-[20px] py-[14px]'
            value={getRewardValue('Free Delivery')}
            onWheel={(e) => e.currentTarget.blur()}
            onChange={(e) => handleRewardChange(e, 'Free Delivery')}
          />
        </div>
        <div className='mb-2'>
          <FileUploader
            token={token}
            fileUrls={formData.tier_image ? [formData.tier_image] : []}
            label={<h3 className='font-medium mb-2'> Upload image </h3>}
            setFileUrls={(k) => setFormData({ ...formData, tier_image: k[0] ?? null })}
          />
        </div>

        <div className='mb-[24px]'>
          <p className='font-medium text-sm'>Complimentary Item</p>
          <Select
            multiple
            hasSearch
            placeholder='Select Complimentary Item'
            values={formData.complimentary_meals.map((m) => m.id)}
            options={mealsList}
            onChange={(e) =>
              setFormData({
                ...formData,
                complimentary_meals: e.map((id: string) => ({
                  id: id,
                  ...getMealNameAndImages(id, complimentaryMeals)
                }))
              })
            }
          />
        </div>
      </BaseModal>
    </div>
  );
};

export default CreateLevelModal;
